import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
    getFullUnassignedPlanByRouteIdThunk,
    getKpiGetPointProgressThunk,
    getPlanGetByPointIdThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";

import { usePoint } from "@/domains/point";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { BlockList } from "@/shared/pageComposition/BlockList";
import { Meta } from "@/shared/seo";
import { PrimaryText } from "@/shared/ui/Typography";

import { AboutPointModel, getAboutPointBlocks } from "./AboutPoint.blocks";
import { getAboutPointCapabilities } from "./AboutPoint.policy";
import { AboutPointPageSkeleton } from "./AboutPointPage.skeleton";
import { StyledAPContainer } from "./Styled";

const AboutPoint = () => {
    const role = useProtectedUserRoleSelector();
    const capabilities = useMemo(() => getAboutPointCapabilities(role), [role]);

    const { currentRoute, routeList } = useAppSelector(
        (state) => state.manager,
    );

    const { pointId } = useParams();
    const { point, pointLoading } = usePoint(pointId);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!pointId) {
            return;
        }

        dispatch(
            getPlanGetByPointIdThunk({
                pointId,
                targetDate: dayjs().format(backendDateFormat.date),
            }),
        );
        dispatch(
            getKpiGetPointProgressThunk({
                pointId,
                targetDate: dayjs().format(backendDateFormat.date),
            }),
        );

        if (capabilities.canFetchUnassignedPlan) {
            const userRoute = routeList?.[currentRoute]?.uuid;

            if (userRoute) {
                dispatch(
                    getFullUnassignedPlanByRouteIdThunk({
                        routeId: userRoute,
                        targetDate: dayjs().format(backendDateFormat.date),
                    }),
                );
            }
        }
    }, [
        capabilities.canFetchUnassignedPlan,
        currentRoute,
        dispatch,
        pointId,
        routeList,
    ]);

    const model: AboutPointModel | null = useMemo(() => {
        if (!point) return null;
        return { capabilities, point };
    }, [capabilities, point]);

    const blocks = useMemo(
        () => (model ? getAboutPointBlocks(model) : []),
        [model],
    );

    const content = useMemo(() => {
        if (pointLoading) {
            return <AboutPointPageSkeleton />;
        }

        if (!point || !model || !blocks) {
            return (
                <StyledAPContainer>
                    <PrimaryText
                        textAlign="center"
                        fontSize={16}
                        style={{ margin: "auto" }}
                    >
                        Інформація відсутня
                    </PrimaryText>
                </StyledAPContainer>
            );
        }

        return (
            <StyledAPContainer>
                <BlockList blocks={blocks} model={model} />
            </StyledAPContainer>
        );
    }, [blocks, model, point, pointLoading]);

    return (
        <>
            <Meta title="TT Info" />
            <Header title="Про ТТ" back titleFontSize={16} />

            {content}
        </>
    );
};

export default AboutPoint;
